import type { CalibrationEndedBy, LearningStance, ProcessingMode } from './desktop-api';

/**
 * Calibration (owner, 2026-10-30; docs/planning/every-document-calibration.md): a job's first phase,
 * on for every new job. While it is on, the documents list asks about review work and teaching
 * examples together, without saying which, and shows no counts. It turns itself off once the job is
 * ready, or when the operator says so in Learning; learning goes on either way, and it never changes
 * what needs review (the Review thresholds decide that).
 *
 * Stored as times on the job's learning stance, never a flag: "since when" is what Learning prints.
 */
export const isCalibrating = (stance: LearningStance | null | undefined): boolean =>
  Boolean(stance?.calibrationStartedAt) && !stance?.calibrationEndedAt;

/** The job is on Autopilot (the retired straight-through value too), as the engine's `OnAutopilot` reads it. */
const onAutopilot = (mode: ProcessingMode | null | undefined): boolean =>
  mode === 'ai-reviewed' || mode === 'straight-through';

/**
 * Calibration is paused while the job is on Autopilot (owner, 2026-10-31): Autopilot asks no one, so the list
 * asks no teaching examples, "next" never walks to them, and the title row shows the review count instead of the
 * Calibration tag. The stance is untouched, so it resumes when the job goes back to Manual.
 */
export const calibrationPaused = (
  stance: LearningStance | null | undefined,
  mode: ProcessingMode | null | undefined,
): boolean => isCalibrating(stance) && onAutopilot(mode);

/** Calibration asks, and its tag shows: on, and not paused by Autopilot. */
export const calibrationAsks = (
  stance: LearningStance | null | undefined,
  mode: ProcessingMode | null | undefined,
): boolean => isCalibrating(stance) && !onAutopilot(mode);

/**
 * A job made on Autopilot (a template, Content Central, a setup file, a legacy import or New job) starts without
 * Calibration (owner, 2026-10-31). The engine's own creation paths say the same.
 */
export const startsInCalibration = (mode: ProcessingMode | null | undefined): boolean =>
  !onAutopilot(mode);

/** The stance with Calibration on from `at` (a new job, or the operator turning it back on). */
export const calibrationStarted = (
  stance: LearningStance | null | undefined,
  at: string,
): LearningStance => ({
  ...stance,
  calibrationStartedAt: at,
  calibrationEndedAt: null,
  calibrationEndedBy: null,
});

/** The stance with Calibration off from `at`. Ending it twice keeps the first time and who ended it. */
export const calibrationEnded = (
  stance: LearningStance | null | undefined,
  at: string,
  by: CalibrationEndedBy,
): LearningStance =>
  stance?.calibrationEndedAt
    ? { ...stance }
    : { ...stance, calibrationEndedAt: at, calibrationEndedBy: by };
